import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDate } from '../utils/dateFormat';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  MessageSquareQuote,
  Send,
  Star,
  Check,
  X,
  Edit,
  Download,
  FileText,
  Loader2,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  RefreshCw,
  Image,
  Mail,
  Plus
} from 'lucide-react';

const STATUS_CONFIG = {
  pending_request: { color: 'bg-amber-100 text-amber-800', label: 'Pending', icon: Clock },
  submitted: { color: 'bg-blue-100 text-blue-800', label: 'Submitted', icon: MessageSquareQuote },
  approved: { color: 'bg-emerald-100 text-emerald-800', label: 'Approved', icon: CheckCircle },
  rejected: { color: 'bg-red-100 text-red-800', label: 'Rejected', icon: XCircle },
  edit_requested: { color: 'bg-orange-100 text-orange-800', label: 'Edit Requested', icon: Edit }
};

// Star Rating Display Component
function StarRating({ rating, size = 'md' }) {
  const sizeClass = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(star => (
        <Star 
          key={star}
          className={`${sizeClass} ${star <= rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
        />
      ))}
    </div>
  );
}

export default function AdminTestimonials() {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [testimonials, setTestimonials] = useState([]);
  const [stats, setStats] = useState({});
  const [activeTab, setActiveTab] = useState('submitted');
  const [eligibleUsers, setEligibleUsers] = useState([]);
  
  // Dialog states
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [showManualDialog, setShowManualDialog] = useState(false);
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  
  // Manual testimonial form
  const [manualForm, setManualForm] = useState({ firstName: '', lastName: '', email: '', role: '', company: '', text: '', rating: 5 });
  const [savingManual, setSavingManual] = useState(false);
  
  // Form states
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [customMessage, setCustomMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [reviewAction, setReviewAction] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [editRequestMessage, setEditRequestMessage] = useState('');
  const [exporting, setExporting] = useState(false);
  
  useEffect(() => {
    fetchTestimonials();
  }, [activeTab]);
  
  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/admin/testimonials?status=${activeTab}`);
      setTestimonials(response.data.testimonials || []);
      setStats(response.data.stats || {});
    } catch (error) {
      toast.error('Failed to load testimonials');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchEligibleUsers = async () => {
    try {
      const response = await axios.get('/api/admin/testimonials/eligible-users');
      setEligibleUsers(response.data.users || []);
    } catch (error) {
      toast.error('Failed to load eligible users');
      console.error(error);
    }
  };
  
  const openRequestDialog = async () => {
    await fetchEligibleUsers();
    setShowRequestDialog(true);
  };
  
  const sendTestimonialRequests = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }
    
    setSending(true);
    try {
      const response = await axios.post('/api/admin/testimonials/request-bulk', {
        userIds: selectedUsers,
        customMessage: customMessage || null
      });
      
      toast.success(response.data.message);
      if (response.data.alreadyRequested > 0) {
        toast.info(`${response.data.alreadyRequested} users already had pending requests`);
      }
      
      setShowRequestDialog(false);
      setSelectedUsers([]);
      setCustomMessage('');
      fetchTestimonials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send requests');
    } finally {
      setSending(false);
    }
  };
  
  const openReviewDialog = (testimonial) => {
    setSelectedTestimonial(testimonial);
    setReviewAction('');
    setAdminNotes('');
    setEditRequestMessage('');
    setShowReviewDialog(true);
  };
  
  const submitReview = async () => {
    if (!reviewAction) {
      toast.error('Please select an action');
      return;
    }
    
    if (reviewAction === 'edit_requested' && !editRequestMessage.trim()) {
      toast.error('Please provide feedback for the edit request');
      return;
    }
    
    try {
      await axios.put(`/api/admin/testimonials/${selectedTestimonial.id}/status`, {
        status: reviewAction,
        adminNotes: adminNotes || null,
        editRequestMessage: reviewAction === 'edit_requested' ? editRequestMessage : null
      });
      
      toast.success(`Testimonial ${reviewAction.replace('_', ' ')}`);
      setShowReviewDialog(false);
      fetchTestimonials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update testimonial');
    }
  };
  
  const exportTestimonials = async (format) => {
    setExporting(true);
    try {
      if (format === 'csv') {
        const response = await axios.get('/api/admin/testimonials/export/csv?status=approved', {
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'testimonials_export.csv');
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else if (format === 'json') {
        const response = await axios.get('/api/admin/testimonials/export/json?status=approved');
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'testimonials_export.json');
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else if (format === 'pdf') {
        const response = await axios.get('/api/admin/testimonials/export/pdf?status=approved', {
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'testimonials_export.pdf');
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export testimonials');
    } finally {
      setExporting(false);
    }
  };
  
  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };
  
  const selectAllBothComplete = () => {
    const bothComplete = eligibleUsers.filter(u => u.bothComplete).map(u => u.id);
    setSelectedUsers(bothComplete);
  };
  
  return (
    <div data-testid="admin-testimonials-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <MessageSquareQuote className="w-6 h-6 text-primary" />
            Testimonials
          </h1>
          <p className="text-muted-foreground">Collect and manage participant testimonials</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => fetchTestimonials()}
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button variant="outline" onClick={() => setShowManualDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Testimonial
          </Button>
          <Button onClick={openRequestDialog} data-testid="request-testimonial-btn">
            <Send className="w-4 h-4 mr-2" />
            Request Testimonial
          </Button>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid md:grid-cols-5 gap-4 mb-8">
        <Card className="cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => setActiveTab('pending_request')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold">{stats.pending_request || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => setActiveTab('submitted')}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">To Review</p>
                  <p className="text-2xl font-bold">{stats.submitted || 0}</p>
                </div>
                <MessageSquareQuote className="w-8 h-8 text-blue-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => setActiveTab('edit_requested')}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Edit Requested</p>
                  <p className="text-2xl font-bold">{stats.edit_requested || 0}</p>
                </div>
                <Edit className="w-8 h-8 text-orange-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => setActiveTab('approved')}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Approved</p>
                  <p className="text-2xl font-bold">{stats.approved || 0}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-emerald-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => setActiveTab('rejected')}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Rejected</p>
                  <p className="text-2xl font-bold">{stats.rejected || 0}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Export Buttons */}
        {stats.approved > 0 && (
          <div className="flex items-center gap-2 mb-6">
            <span className="text-sm text-muted-foreground mr-2">Export Approved:</span>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => exportTestimonials('csv')}
              disabled={exporting}
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-1" />
              CSV
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => exportTestimonials('json')}
              disabled={exporting}
              data-testid="export-json-btn"
            >
              <Download className="w-4 h-4 mr-1" />
              JSON
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => exportTestimonials('pdf')}
              disabled={exporting}
              data-testid="export-pdf-btn"
            >
              <FileText className="w-4 h-4 mr-1" />
              PDF
            </Button>
          </div>
        )}
        
        {/* Testimonials Table */}
        <Card>
          <CardHeader>
            <CardTitle>
              {activeTab === 'submitted' ? 'Review Queue' : 
               activeTab === 'approved' ? 'Approved Testimonials' :
               activeTab === 'pending_request' ? 'Pending Requests' :
               activeTab === 'edit_requested' ? 'Edit Requested' : 'Rejected'}
            </CardTitle>
            <CardDescription>
              {activeTab === 'submitted' ? 'Testimonials waiting for your review' :
               activeTab === 'approved' ? 'Testimonials ready for public display' :
               activeTab === 'pending_request' ? 'Requests sent, awaiting submission' :
               activeTab === 'edit_requested' ? 'Waiting for participant revisions' : 'Declined testimonials'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : testimonials.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquareQuote className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="font-semibold mb-2">No testimonials</h3>
                <p className="text-muted-foreground mb-4">
                  {activeTab === 'submitted' ? 'No testimonials waiting for review' :
                   'No testimonials in this category'}
                </p>
                {activeTab === 'pending_request' && (
                  <Button onClick={openRequestDialog}>
                    <Send className="w-4 h-4 mr-2" />
                    Request Testimonials
                  </Button>
                )}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Participant</TableHead>
                    {activeTab !== 'pending_request' && <TableHead>Rating</TableHead>}
                    {activeTab !== 'pending_request' && <TableHead>Testimonial</TableHead>}
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {testimonials.map(testimonial => {
                    const status = STATUS_CONFIG[testimonial.status] || STATUS_CONFIG.pending_request;
                    const StatusIcon = status.icon;
                    
                    return (
                      <TableRow key={testimonial.id} data-testid={`testimonial-row-${testimonial.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {testimonial.photoBase64 ? (
                              <img 
                                src={`data:${testimonial.photoMimeType};base64,${testimonial.photoBase64}`}
                                alt=""
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/80 to-primary flex items-center justify-center text-primary-foreground font-medium">
                                {testimonial.firstName?.[0]}{testimonial.lastName?.[0]}
                              </div>
                            )}
                            <div>
                              <p className="font-medium">{testimonial.firstName} {testimonial.lastName}</p>
                              <p className="text-sm text-muted-foreground">{testimonial.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        {activeTab !== 'pending_request' && (
                          <TableCell>
                            {testimonial.rating ? (
                              <StarRating rating={testimonial.rating} size="sm" />
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                        )}
                        {activeTab !== 'pending_request' && (
                          <TableCell className="max-w-[300px]">
                            {testimonial.text ? (
                              <p className="truncate text-sm">{testimonial.text}</p>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                        )}
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {testimonial.submittedAt 
                              ? formatDate(testimonial.submittedAt)
                              : formatDate(testimonial.requestedAt)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge className={status.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {status.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openReviewDialog(testimonial)}
                            data-testid={`review-btn-${testimonial.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            {activeTab === 'submitted' ? 'Review' : 'View'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      
      {/* Request Testimonial Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Request Testimonials</DialogTitle>
            <DialogDescription>
              Send testimonial requests to participants who have completed the program
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {eligibleUsers.length} eligible participants
              </p>
              <Button variant="outline" size="sm" onClick={selectAllBothComplete}>
                Select All Complete
              </Button>
            </div>
            
            <div className="max-h-[300px] overflow-y-auto border rounded-lg">
              {eligibleUsers.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No eligible users available</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12"></TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Assessments</TableHead>
                      <TableHead>Application</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {eligibleUsers.map(user => (
                      <TableRow 
                        key={user.id}
                        className="cursor-pointer"
                        onClick={() => toggleUserSelection(user.id)}
                      >
                        <TableCell>
                          <Checkbox 
                            checked={selectedUsers.includes(user.id)}
                            onCheckedChange={() => toggleUserSelection(user.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{user.firstName} {user.lastName}</p>
                            <p className="text-sm text-muted-foreground">{user.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Badge variant={user.hasFearAssessment ? "default" : "outline"} className="text-xs">
                              EDGE
                            </Badge>
                            <Badge variant={user.hasArchetypeAssessment ? "default" : "outline"} className="text-xs">
                              CORE
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          {user.applicationStatus ? (
                            <Badge variant="outline" className="text-xs">
                              {user.applicationStatus}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">
                Custom Message <span className="text-muted-foreground">(optional)</span>
              </label>
              <Textarea
                placeholder="Add a personalized message to include in the email..."
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                rows={3}
              />
            </div>
            
            {selectedUsers.length > 0 && (
              <p className="text-sm text-primary font-medium">
                {selectedUsers.length} participant{selectedUsers.length > 1 ? 's' : ''} selected
              </p>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequestDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={sendTestimonialRequests} 
              disabled={sending || selectedUsers.length === 0}
              data-testid="send-requests-btn"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Send {selectedUsers.length} Request{selectedUsers.length !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Review Testimonial Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selectedTestimonial?.status === 'submitted' ? 'Review Testimonial' : 'Testimonial Details'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedTestimonial && (
            <div className="py-4 space-y-6">
              {/* Participant Info */}
              <div className="flex items-center gap-4">
                {selectedTestimonial.photoBase64 ? (
                  <img 
                    src={`data:${selectedTestimonial.photoMimeType};base64,${selectedTestimonial.photoBase64}`}
                    alt=""
                    className="w-16 h-16 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary/80 to-primary flex items-center justify-center text-primary-foreground font-semibold text-xl">
                    {selectedTestimonial.firstName?.[0]}{selectedTestimonial.lastName?.[0]}
                  </div>
                )}
                <div>
                  <h3 className="font-semibold text-lg">
                    {selectedTestimonial.firstName} {selectedTestimonial.lastName}
                  </h3>
                  <p className="text-muted-foreground">{selectedTestimonial.email}</p>
                </div>
              </div>
              
              {/* Rating & Testimonial */}
              {selectedTestimonial.rating && (
                <div>
                  <label className="text-sm font-medium mb-2 block">Rating</label>
                  <StarRating rating={selectedTestimonial.rating} />
                </div>
              )}
              
              {selectedTestimonial.text && (
                <div>
                  <label className="text-sm font-medium mb-2 block">Testimonial</label>
                  <div className="p-4 bg-muted/50 rounded-lg border">
                    <p className="italic text-foreground leading-relaxed">
                      &ldquo;{selectedTestimonial.text}&rdquo;
                    </p>
                  </div>
                </div>
              )}
              
              {/* Photo */}
              {selectedTestimonial.photoBase64 && (
                <div>
                  <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    Attached Photo
                  </label>
                  <img 
                    src={`data:${selectedTestimonial.photoMimeType};base64,${selectedTestimonial.photoBase64}`}
                    alt="Testimonial"
                    className="max-w-[200px] rounded-lg border"
                  />
                </div>
              )}
              
              {/* Previous Edit Request */}
              {selectedTestimonial.editRequestMessage && (
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-sm font-medium text-orange-800 mb-1">Previous Edit Request:</p>
                  <p className="text-sm text-orange-700">{selectedTestimonial.editRequestMessage}</p>
                </div>
              )}
              
              {/* Review Actions (only for submitted) */}
              {selectedTestimonial.status === 'submitted' && (
                <>
                  <div className="border-t pt-4">
                    <label className="text-sm font-medium mb-3 block">Review Action</label>
                    <div className="flex gap-3">
                      <Button
                        variant={reviewAction === 'approved' ? 'default' : 'outline'}
                        onClick={() => setReviewAction('approved')}
                        className={reviewAction === 'approved' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Approve
                      </Button>
                      <Button
                        variant={reviewAction === 'edit_requested' ? 'default' : 'outline'}
                        onClick={() => setReviewAction('edit_requested')}
                        className={reviewAction === 'edit_requested' ? 'bg-orange-600 hover:bg-orange-700' : ''}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Request Edit
                      </Button>
                      <Button
                        variant={reviewAction === 'rejected' ? 'default' : 'outline'}
                        onClick={() => setReviewAction('rejected')}
                        className={reviewAction === 'rejected' ? 'bg-red-600 hover:bg-red-700' : ''}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  </div>
                  
                  {reviewAction === 'edit_requested' && (
                    <div>
                      <label className="text-sm font-medium mb-2 block">
                        Edit Request Message *
                      </label>
                      <Textarea
                        placeholder="Let them know what changes you'd like..."
                        value={editRequestMessage}
                        onChange={(e) => setEditRequestMessage(e.target.value)}
                        rows={3}
                      />
                    </div>
                  )}
                  
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      Admin Notes <span className="text-muted-foreground">(internal)</span>
                    </label>
                    <Textarea
                      placeholder="Add internal notes..."
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      rows={2}
                    />
                  </div>
                </>
              )}
              
              {/* View Mode Info */}
              {selectedTestimonial.status !== 'submitted' && selectedTestimonial.adminNotes && (
                <div>
                  <label className="text-sm font-medium mb-2 block">Admin Notes</label>
                  <p className="text-sm text-muted-foreground p-3 bg-muted rounded-lg">
                    {selectedTestimonial.adminNotes}
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReviewDialog(false)}>
              {selectedTestimonial?.status === 'submitted' ? 'Cancel' : 'Close'}
            </Button>
            {selectedTestimonial?.status === 'submitted' && (
              <Button 
                onClick={submitReview} 
                disabled={!reviewAction}
                data-testid="submit-review-btn"
              >
                Submit Review
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Testimonial Dialog */}
      <Dialog open={showManualDialog} onOpenChange={setShowManualDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Testimonial</DialogTitle>
            <DialogDescription>Manually add a testimonial from a participant or external source.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input value={manualForm.firstName} onChange={(e) => setManualForm({...manualForm, firstName: e.target.value})} placeholder="First name" />
              </div>
              <div className="space-y-2">
                <Label>Last Name</Label>
                <Input value={manualForm.lastName} onChange={(e) => setManualForm({...manualForm, lastName: e.target.value})} placeholder="Last name" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Role / Title</Label>
                <Input value={manualForm.role} onChange={(e) => setManualForm({...manualForm, role: e.target.value})} placeholder="CEO, Director, etc." />
              </div>
              <div className="space-y-2">
                <Label>Company</Label>
                <Input value={manualForm.company} onChange={(e) => setManualForm({...manualForm, company: e.target.value})} placeholder="Company name" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={manualForm.email} onChange={(e) => setManualForm({...manualForm, email: e.target.value})} placeholder="Optional" />
            </div>
            <div className="space-y-2">
              <Label>Testimonial Text *</Label>
              <Textarea value={manualForm.text} onChange={(e) => setManualForm({...manualForm, text: e.target.value})} placeholder="Their testimonial..." rows={4} />
            </div>
            <div className="space-y-2">
              <Label>Rating (1-5)</Label>
              <div className="flex gap-1">
                {[1,2,3,4,5].map(r => (
                  <button key={r} onClick={() => setManualForm({...manualForm, rating: r})} className={`p-1 ${manualForm.rating >= r ? 'text-amber-500' : 'text-gray-300'}`}>
                    <Star className="w-6 h-6 fill-current" />
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManualDialog(false)}>Cancel</Button>
            <Button disabled={savingManual || !manualForm.firstName || !manualForm.text} onClick={async () => {
              setSavingManual(true);
              try {
                await axios.post('/api/admin/testimonials/manual', manualForm);
                toast.success('Testimonial added');
                setShowManualDialog(false);
                setManualForm({ firstName: '', lastName: '', email: '', role: '', company: '', text: '', rating: 5 });
                fetchTestimonials();
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Failed to add testimonial');
              } finally {
                setSavingManual(false);
              }
            }}>
              {savingManual ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Add Testimonial
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
